import { shallowEqual } from "react-redux";
import { useBnbSelector } from "../../redux/store";
import type { ServiceType } from "../../../../../common-deprecated/types/SettingsType";
import { ServiceTypeEnum } from "../../../../../common-deprecated/types/SettingsType";
import { enabledServicesSelector } from "../../utils/selectorUtils";

type UseServicesType = {
    /**
     * Defines which and the order of services that should be rendered
     */
    services: ServiceType[];
    showTradeIn: boolean;
    showFinance: boolean;
    showInsurance: boolean;
    activeServicesCount: number;
    isServiceConfigured: (service: ServiceTypeEnum) => boolean;
};

// These services can be hidden by separate TridionSettings
export const isGuardedService = (type: ServiceTypeEnum): boolean =>
    [ServiceTypeEnum.Tradein, ServiceTypeEnum.Finance, ServiceTypeEnum.Insurance].includes(type);

const useServices = (): UseServicesType => {
    const { showFinance, showInsurance, showTradeIn } = useBnbSelector(enabledServicesSelector, shallowEqual);
    const tradeInPrice = useBnbSelector((state) => state.tradeInAemExternalData.data.vehicleValue);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const insuranceTotal = useBnbSelector((state) => Number(state.insuranceRate.total));
    const configuredServices = useBnbSelector((state) => state.bnbSettings.services);

    // remove configured service when disabled elsewhere
    const services = configuredServices.reduce((acc: ServiceType[], service) => {
        if (
            !isGuardedService(service.type) ||
            (service.type === ServiceTypeEnum.Tradein && showTradeIn) ||
            (service.type === ServiceTypeEnum.Finance && showFinance) ||
            (service.type === ServiceTypeEnum.Insurance && showInsurance)
        ) {
            acc.push(service);
        }
        return acc;
    }, []);

    const isServiceConfigured = (service: ServiceTypeEnum): boolean =>
        !!services.filter(({ type }) => type === service).length;

    // Check for finance packs and add them to services if they are present.

    // calculate amount of active services
    const activeServicesCount = [
        showFinance && financeOption === "monthly",
        showTradeIn && tradeInPrice,
        isServiceConfigured(ServiceTypeEnum.Insurance) && insuranceTotal,
        isServiceConfigured(ServiceTypeEnum.Financepacks),
    ].reduce((count: number, value) => count + Number(!!value), 0);

    return {
        services,
        showTradeIn,
        showFinance,
        showInsurance,
        activeServicesCount,
        isServiceConfigured,
    };
};

export default useServices;
